import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguagePicker } from './LanguagePicker';
import { PlyEZMark } from './BrandLogo';

/**
 * Public footer. Five real columns:
 *   - brand + tagline + locale picker
 *   - product (pricing / compare / demo / status)
 *   - legal (terms / privacy / cookies / DPA / subprocessors / AUP / data sourcing)
 *   - company (about / careers / contact / changelog / security)
 *   - resources (OSS components)
 */
export function SiteFooter() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border bg-background mt-auto">
      <div className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-5 gap-10 text-sm">
        <div className="lg:col-span-2">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <PlyEZMark size={24} />
            <div className="font-display font-semibold text-base">{t('brand')}</div>
          </Link>
          <p className="mt-3 text-muted-foreground leading-relaxed max-w-sm">{t('tagline')}</p>
          <div className="mt-5">
            <LanguagePicker compact />
          </div>
        </div>

        <FooterCol
          title={t('footer.product')}
          links={[
            { label: t('nav.pricing'), to: '/pricing' },
            { label: t('nav.compare'), to: '/compare' },
            { label: t('nav.demo'), to: '/demo' },
            { label: t('nav.security'), to: '/security' },
            { label: t('footer.statusPage'), to: '/status' },
          ]}
        />

        <FooterCol
          title={t('footer.legal')}
          links={[
            { label: t('footer.terms'), to: '/legal/terms' },
            { label: t('footer.privacy'), to: '/legal/privacy' },
            { label: t('footer.cookies'), to: '/legal/cookies' },
            { label: t('footer.dpa'), to: '/legal/dpa' },
            { label: t('footer.subprocessors'), to: '/legal/subprocessors' },
            { label: t('footer.aup'), to: '/legal/aup' },
            { label: t('footer.dataSourcing'), to: '/legal/data-sourcing' },
            { label: t('footer.sla'), to: '/legal/sla' },
          ]}
        />

        <FooterCol
          title={t('footer.company')}
          links={[
            { label: t('footer.about'), to: '/about' },
            { label: t('footer.careers'), to: '/careers' },
            { label: t('footer.contact'), to: '/contact' },
            { label: t('footer.changelog'), to: '/changelog' },
            { label: t('footer.oss'), to: '/oss' },
          ]}
        />
      </div>

      <div className="border-t border-border py-5 text-center text-xs text-muted-foreground">
        {t('footer.copyright')}
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: Array<{ label: string; to: string }> }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{title}</div>
      <ul className="mt-3 space-y-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-foreground/80 hover:text-foreground transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
